import { AnyDragSurfaceProjector, AutoAdaptProductSpecificationsSystem, CanvasTo3DDragConverterSystem, DragSurfaceCanHostSelector, DragSurfaceCanHostWithAutoAdaptSelector, DragSystem, LegacyCanvasTo3DDragConverterSystem, StlSystemIds, } from "@inter-ikea-core/core";
var DRAG_RELATED_SYSTEM_IDS = [
    StlSystemIds.DRAG_SYSTEM_ID,
    StlSystemIds.LEGACY_CANVAS_TO_3D_DRAG_CONVERTER_SYSTEM,
    StlSystemIds.CANVAS_TO_3D_DRAG_CONVERTER_SYSTEM,
];
/**
 * Contains some handy functions for adding and removing dragging functionality to Core.
 */
export var DragUtils = {
    /**
     * Creates a drag system with auto-adapt functionality. If you need more control of the drag strategies,
     * you can create your own drag system and add the strategies you need.
     *
     * This function doesn't register the system with the entity manager.
     *
     * @param coreServices Core's services
     * @param enableMultiDrag whether or not the created DragSystem should support dragging multiple selected entities
     * @returns the newly created drag system.
     */
    createAutoAdaptDragSystem: function (coreServices, enableMultiDrag) {
        return DragSystem.createAutoAdaptDragSystem(coreServices, enableMultiDrag);
    },
    /**
     * Creates a drag system without auto-adapt functionality. If you need more control of the drag strategies,
     * you can create your own drag system and add the strategies you need.
     *
     * This function doesn't register the system with the entity manager.
     *
     * @param coreServices Core's services
     * @param enableMultiDrag whether or not the created DragSystem should support dragging multiple selected entities
     * @returns the newly created drag system.
     */
    createStandardDragSystem: function (coreServices, enableMultiDrag) {
        return DragSystem.createStandardDragSystem(coreServices, enableMultiDrag);
    },
    /**
     * Adds drag to Core. This includes creating and registering the drag system and all the drag support systems.
     * Will remove all drag related systems before installing the new ones.
     *
     * @param coreServices Core's services
     * @param withAutoAdapt whether to use auto-adapt functionality
     * @param enableMultiDrag whether or not the created DragSystem should support dragging multiple selected entities
     */
    addDrag: function (coreServices, withAutoAdapt, enableMultiDrag) {
        setupDragSystems(coreServices, withAutoAdapt, enableMultiDrag);
        var projector = getDragProjector(coreServices, withAutoAdapt);
        var converterSystem = new CanvasTo3DDragConverterSystem(coreServices.entityManager, coreServices.sceneManager, projector);
        coreServices.entityManager.registerSystem(converterSystem);
    },
    /**
     * Adds legacy drag to Core. Will remove all drag related systems before installing the new ones.
     * You probably should move over to {@link addDrag} instead of using this function.
     *
     * @param coreServices Core's services
     * @param withAutoAdapt whether to use auto-adapt functionality
     * @param enableMultiDrag whether or not the created DragSystem should support dragging multiple selected entities
     */
    addLegacyDrag: function (coreServices, withAutoAdapt, enableMultiDrag) {
        setupDragSystems(coreServices, withAutoAdapt, enableMultiDrag);
        var projector = getDragProjector(coreServices, withAutoAdapt);
        var converterSystem = new LegacyCanvasTo3DDragConverterSystem(coreServices.entityManager, coreServices.sceneManager, projector);
        coreServices.entityManager.registerSystem(converterSystem);
    },
    /**
     * Remove all drag related systems.
     * @param coreServices Core's services
     */
    removeDragSystems: function (coreServices) {
        var entityManager = coreServices.entityManager;
        entityManager
            .getSystemsRegistered()
            .filter(function (system) { return DRAG_RELATED_SYSTEM_IDS.includes(system.id); })
            .forEach(function (system) { return entityManager.unregisterSystem(system); });
    },
};
function startNecessarySystemsForAutoAdapt(_a) {
    var entityManager = _a.entityManager, productManager = _a.productManager;
    var system = entityManager
        .getSystemsRegistered()
        .find(function (system) {
        return system.id ===
            StlSystemIds.AUTO_ADAPT_PRODUCT_SPECIFICATION_SYSTEM_ID;
    });
    if (!system) {
        var newSystem = new AutoAdaptProductSpecificationsSystem(productManager);
        entityManager.registerSystem(newSystem);
    }
}
function setupDragSystems(coreServices, withAutoAdapt, enableMultiDrag) {
    DragUtils.removeDragSystems(coreServices);
    var dragSystem = withAutoAdapt
        ? DragSystem.createAutoAdaptDragSystem(coreServices, enableMultiDrag)
        : DragSystem.createStandardDragSystem(coreServices, enableMultiDrag);
    coreServices.entityManager.registerSystem(dragSystem);
    if (withAutoAdapt) {
        startNecessarySystemsForAutoAdapt(coreServices);
    }
}
function getDragProjector(coreServices, withAutoAdapt) {
    var selector = withAutoAdapt
        ? new DragSurfaceCanHostWithAutoAdaptSelector(coreServices.entityManager)
        : new DragSurfaceCanHostSelector();
    var projector = new AnyDragSurfaceProjector(coreServices.sceneManager, coreServices.entityManager, selector);
    return projector;
}
//# sourceMappingURL=DragUtils.js.map